import { chakra, Flex, FlexProps, Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/react'
import { ArrowDown } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { ScrollingList } from '~/components/shared/ScrollingList'
import { CallListItem } from '~/pages/Calls/CallListItem'
import {
  useCallTableStyles,
  useTableHeaderCellStyles,
} from '~/pages/Calls/componentStyles/CallsTable.style'
import {
  CallTableColumn,
  CallTableColumnSorting,
} from '~/pages/Calls/data/CallTableData'
import { SortDirection } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

import { Call } from '@mangovoice/types'

import { TCallAnalyticsTab } from './CallDetails/CallDetailsAnalyticsContent'

interface TableHeaderCellProps extends FlexProps {
  columnType: CallTableColumn
  sortDirection?: SortDirection
  sortable?: boolean
  onSortClick?: (
    columnType: CallTableColumn,
    currentSortDirection?: SortDirection,
  ) => void
  tableStyles: ReturnType<typeof useCallTableStyles>
}

// Style types used in the style system (subset of CallTableColumn)
type CallTableStyleType =
  | CallTableColumn.DateTime
  | CallTableColumn.Outcome
  | CallTableColumn.Contact
  | CallTableColumn.Duration
  | CallTableColumn.Transcript

const getCallTableHeaderCellData = (
  t: (key: string) => string,
): Record<
  CallTableColumn,
  { title: string; styleType: CallTableStyleType }
> => ({
  [CallTableColumn.DateTime]: {
    title: t('callsTable.columns.dateTime'),
    styleType: CallTableColumn.DateTime,
  },
  [CallTableColumn.Outcome]: {
    title: t('callsTable.columns.outcome'),
    styleType: CallTableColumn.Outcome,
  },
  [CallTableColumn.To]: {
    title: t('callsTable.columns.to'),
    styleType: CallTableColumn.Contact,
  },
  [CallTableColumn.From]: {
    title: t('callsTable.columns.from'),
    styleType: CallTableColumn.Contact,
  },
  [CallTableColumn.ToFrom]: {
    title: t('callsTable.columns.toFrom'),
    styleType: CallTableColumn.Contact,
  },
  [CallTableColumn.Duration]: {
    title: t('callsTable.columns.duration'),
    styleType: CallTableColumn.Duration,
  },
  [CallTableColumn.Transcript]: {
    title: t('callsTable.columns.transcript'),
    styleType: CallTableColumn.Transcript,
  },
  [CallTableColumn.Contact]: {
    title: t('callsTable.columns.contact'),
    styleType: CallTableColumn.Contact,
  },
})

const SORT_ICON_CLASS = 'calls-table-sort-icon'

const TableHeaderCell = ({
  columnType,
  sortDirection,
  sortable,
  onSortClick,
  tableStyles,
}: TableHeaderCellProps) => {
  const { t } = useTranslation()
  const { title, styleType } = getCallTableHeaderCellData(t)[columnType]
  const columnStyle = tableStyles[styleType]
  const styles = useTableHeaderCellStyles()
  const headerStyle = styles[styleType]

  const iconRotation = sortDirection === SortDirection.Ascending ? 180 : 0

  if (!sortable) {
    return (
      <Flex role="columnheader" css={{ ...columnStyle, ...headerStyle }}>
        <Text as="span" css={styles.headerText}>
          {title}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex
      role="columnheader"
      aria-sort={sortDirection ?? 'none'}
      css={{ ...columnStyle, ...headerStyle }}
    >
      <chakra.button
        type="button"
        onClick={() => onSortClick?.(columnType, sortDirection)}
        display="flex"
        alignItems="center"
        gap="inherit"
        cursor="pointer"
        borderRadius="sm"
        css={{
          [`& .${SORT_ICON_CLASS}`]: {
            opacity: sortDirection ? 1 : 0,
          },
          [`&:hover .${SORT_ICON_CLASS}, &:focus-visible .${SORT_ICON_CLASS}`]:
            { opacity: 1 },
        }}
      >
        <Text as="span" css={styles.headerText}>
          {title}
        </Text>
        <Icon
          as={ArrowDown}
          aria-hidden
          className={SORT_ICON_CLASS}
          css={{
            ...styles.sortIcon,
            transform: `rotate(${iconRotation}deg)`,
          }}
        />
      </chakra.button>
    </Flex>
  )
}

interface CallsTableProps extends FlexProps {
  calls: Call[]
  selectedCallId?: number
  isLoading?: boolean
  tableSize: Size
  columnSorting?: CallTableColumnSorting[]
  onCallSelected?: (callId: number, initialTab?: TCallAnalyticsTab) => void
  onSortableColumnClicked?: (
    columnType: CallTableColumn,
    currentSortDirection?: SortDirection,
  ) => void
  onEndReached?: () => void
}
export const CallsTable = ({
  calls,
  selectedCallId,
  isLoading,
  tableSize,
  columnSorting,
  onCallSelected,
  onSortableColumnClicked,
  onEndReached,
  ...rest
}: CallsTableProps) => {
  const { t } = useTranslation()
  const tableStyles = useCallTableStyles({ size: tableSize })
  const styles = useTableHeaderCellStyles({ size: tableSize })
  const columnSortingMap = useMemo(() => {
    return new Map(
      columnSorting?.map(({ column, direction }) => [column, direction]),
    )
  }, [columnSorting])

  const showTableLoader = isLoading && calls.length === 0

  const tableContent = useMemo(() => {
    if (showTableLoader) {
      return (
        <Flex justify="center" align="center" boxSize="full">
          <LoadingSpinner size={Size.lg} />
        </Flex>
      )
    }

    return (
      <Flex role="rowgroup" css={styles.listContainer}>
        <ScrollingList
          display="flex"
          height="100%"
          width="full"
          bg="bg.subtle"
          itemData={calls}
          showLoaderFooter={isLoading}
          onEndReached={onEndReached}
          scrollbarOptions={{ showOnHoverOnly: true }}
          selectedItemIndex={calls.findIndex(
            (call) => call.id === selectedCallId,
          )}
          onBuildItemContent={(index, call, isSelected) => (
            <CallListItem
              key={index}
              call={call}
              isSelected={isSelected}
              tableSize={tableSize}
              onSelected={() => onCallSelected?.(call.id)}
              isFirst={index === 0 ? true : undefined}
              onTranscriptClick={() => onCallSelected?.(call.id, 'transcript')}
            />
          )}
        />
      </Flex>
    )
  }, [
    calls,
    showTableLoader,
    selectedCallId,
    tableSize,
    onEndReached,
    onCallSelected,
  ])

  return (
    <Flex
      role="table"
      aria-label={t('callsTable.label')}
      css={styles.container}
      {...rest}
    >
      <Flex role="rowgroup" css={styles.tableHeader}>
        <Flex role="row" css={styles.headerColumnWrapper}>
          <TableHeaderCell
            columnType={CallTableColumn.DateTime}
            sortDirection={columnSortingMap.get(CallTableColumn.DateTime)}
            sortable={columnSortingMap.has(CallTableColumn.DateTime)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          <TableHeaderCell
            columnType={CallTableColumn.Outcome}
            sortDirection={columnSortingMap.get(CallTableColumn.Outcome)}
            sortable={columnSortingMap.has(CallTableColumn.Outcome)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          {tableSize == Size.md || tableSize == Size.lg ? (
            <>
              <TableHeaderCell
                columnType={CallTableColumn.From}
                sortDirection={columnSortingMap.get(CallTableColumn.From)}
                sortable={columnSortingMap.has(CallTableColumn.From)}
                onSortClick={onSortableColumnClicked}
                tableStyles={tableStyles}
              />
              <TableHeaderCell
                columnType={CallTableColumn.To}
                sortDirection={columnSortingMap.get(CallTableColumn.To)}
                sortable={columnSortingMap.has(CallTableColumn.To)}
                onSortClick={onSortableColumnClicked}
                tableStyles={tableStyles}
              />
            </>
          ) : (
            <TableHeaderCell
              columnType={CallTableColumn.ToFrom}
              sortDirection={columnSortingMap.get(CallTableColumn.ToFrom)}
              sortable={columnSortingMap.has(CallTableColumn.ToFrom)}
              onSortClick={onSortableColumnClicked}
              tableStyles={tableStyles}
            />
          )}
          <TableHeaderCell
            columnType={CallTableColumn.Duration}
            sortDirection={columnSortingMap.get(CallTableColumn.Duration)}
            sortable={columnSortingMap.has(CallTableColumn.Duration)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          {(tableSize == Size.lg || tableSize == Size.md) && (
            <TableHeaderCell
              columnType={CallTableColumn.Transcript}
              sortDirection={columnSortingMap.get(CallTableColumn.Transcript)}
              sortable={columnSortingMap.has(CallTableColumn.Transcript)}
              onSortClick={onSortableColumnClicked}
              tableStyles={tableStyles}
            />
          )}
        </Flex>
      </Flex>
      {tableContent}
    </Flex>
  )
}
